import type { ScanOutcome, ScanRow } from "../../../../api/roleProvisioning.service.js";
import type { ClientOutcome, ProblemRow } from "./parseCsv.js";

/**
 * Shapes the scan result and the client-side problems into one preview table
 * (migration-plan.md §6.3), and owns the outcome → group / label / selectable
 * rules the preview, confirmation and skipped.csv all lean on.
 */

export type PreviewOutcome = ScanOutcome | ClientOutcome;

export type OutcomeGroup = "insert" | "create" | "no-op" | "skipped";

export type PreviewRow = {
  /** Line numbers are unique per file row, so they key the table and the selection. */
  id: string;
  lineNumber: number;
  email: string;
  outcome: PreviewOutcome;
  group: OutcomeGroup;
  userName: string | null;
  userStatus: string | null;
  organisationName: string | null;
  firmRef: string | null;
  existingRoles: string[];
  rolesToInsert: string[];
  /** Human detail behind the outcome — the org list, the AB companies, the parse problem. */
  detail: string;
  /** The scan row behind a server-side outcome; null for client-side problems. */
  scan: ScanRow | null;
};

export const OUTCOME_LABELS: Record<PreviewOutcome, string> = {
  insert: "Will insert",
  already_has_roles: "Already has roles",
  covered_by_account_owner: "Account owner",
  multiple_organisations: "Multiple organisations",
  organisation_inactive: "Organisation inactive",
  firm_ref_mismatch: "firm_ref mismatch",
  create_and_insert: "Will create",
  user_not_found: "User not found",
  address_book_ambiguous: "Address Book ambiguous",
  address_book_no_organisation: "No organisation for company",
  no_membership: "No membership",
  owner_requires_existing_user: "No account — owner needs existing user",
  invalid_row: "Invalid row",
  duplicate_in_file: "Duplicate in file",
};

export const GROUP_LABELS: Record<OutcomeGroup, string> = {
  insert: "Will insert",
  create: "Will create",
  "no-op": "No-op",
  skipped: "Skipped",
};

export const GROUPS: readonly OutcomeGroup[] = ["insert", "create", "no-op", "skipped"];

export function outcomeGroup(outcome: PreviewOutcome): OutcomeGroup {
  switch (outcome) {
    case "insert":
      return "insert";
    case "create_and_insert":
      return "create";
    case "already_has_roles":
    case "covered_by_account_owner":
      return "no-op";
    default:
      return "skipped";
  }
}

/**
 * Whether a row can be ticked. Insert and create rows always; account-owner
 * rows only behind the explicit "also grant to account owners" toggle; skip
 * outcomes never — there is nothing the apply endpoint could do with them.
 */
export function isSelectable(outcome: PreviewOutcome, includeOwners: boolean): boolean {
  if (outcome === "insert" || outcome === "create_and_insert") {
    return true;
  }
  return outcome === "covered_by_account_owner" && includeOwners;
}

/** The detail column / skipped.csv `detail`: the list CS needs to act on the row. */
export function detailFor(row: ScanRow): string {
  const { organisations, addressBook } = row;
  switch (row.outcome) {
    case "multiple_organisations":
      return organisations.length > 0
        ? `member of: ${organisations.join("; ")}`
        : "member of more than one organisation";
    case "create_and_insert":
    case "address_book_ambiguous":
    case "address_book_no_organisation":
      return addressBook
        ? `${addressBook.contactName} (contact ${addressBook.contactId}) — ${addressBook.companies.join("; ")}`
        : "";
    case "firm_ref_mismatch":
      return row.firmRef ? `not a member of firm_ref ${row.firmRef}` : "";
    case "organisation_inactive":
      return row.organisationName ? `${row.organisationName} is not active` : "";
    case "owner_requires_existing_user":
      return "no Account Services user for this email; account_owner is never created";
    default:
      return "";
  }
}

function fromScan(row: ScanRow): PreviewRow {
  return {
    id: String(row.lineNumber),
    lineNumber: row.lineNumber,
    email: row.email,
    outcome: row.outcome,
    group: outcomeGroup(row.outcome),
    userName: row.userName ?? row.addressBook?.contactName ?? null,
    userStatus: row.userStatus,
    organisationName: row.organisationName,
    firmRef: row.firmRef,
    existingRoles: row.existingRoles ?? [],
    rolesToInsert: row.rolesToInsert ?? [],
    detail: detailFor(row),
    scan: row,
  };
}

function fromProblem(problem: ProblemRow): PreviewRow {
  return {
    id: String(problem.lineNumber),
    lineNumber: problem.lineNumber,
    email: problem.email,
    outcome: problem.outcome,
    group: "skipped",
    userName: null,
    userStatus: null,
    organisationName: null,
    firmRef: null,
    existingRoles: [],
    rolesToInsert: [],
    detail: problem.detail,
    scan: null,
  };
}

/** One row per file row, server and client outcomes interleaved in file order. */
export function buildPreviewRows(scanRows: ScanRow[], problems: ProblemRow[]): PreviewRow[] {
  return [...scanRows.map(fromScan), ...problems.map(fromProblem)].sort(
    (left, right) => left.lineNumber - right.lineNumber,
  );
}

export function countGroups(rows: PreviewRow[]): Record<OutcomeGroup, number> {
  const counts: Record<OutcomeGroup, number> = { insert: 0, create: 0, "no-op": 0, skipped: 0 };
  for (const row of rows) {
    counts[row.group] += 1;
  }
  return counts;
}
